import { Link, useParams } from '@tanstack/react-router';
import type { Project } from '@hangar/shared';
import { useProjects } from '@/api/hooks';
import { formatDate } from '@/lib/format';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { EnvPill, Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AppsSection } from '@/features/apps/AppsSection';
import { liveEnvironments } from '@/features/projects/projectsModel';

function ProjectNotFound({ slug }: { slug: string }) {
  return (
    <EmptyState
      icon="account_tree"
      title="Project not found"
      description={`There is no project with slug “${slug}” in this organization.`}
      action={
        <Button asChild variant="outline">
          <Link to="/">Back to Overview</Link>
        </Button>
      }
    />
  );
}

function ProjectHeaderTop({ slug }: { slug: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Kicker className="text-subtle">Apps &amp; profiles</Kicker>
      <Button asChild variant="outline" size="sm" data-testid="project-settings-link">
        <Link to="/projects/$slug/settings" params={{ slug }}>
          <Icon name="settings" size={16} />
          Settings
        </Link>
      </Button>
    </div>
  );
}

function ProjectHeader({ project }: { project: Project }) {
  return (
    <header className="enter flex flex-col gap-2">
      <ProjectHeaderTop slug={project.slug} />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{project.name}</h1>
        <Tag>{project.slug}</Tag>
        <div className="flex flex-wrap items-center gap-1.5">
          {liveEnvironments(project).map((env) => (
            <EnvPill key={env.id} env={env} />
          ))}
        </div>
      </div>
      {project.description ? <p className="max-w-[72ch] text-sm text-muted-foreground">{project.description}</p> : null}
      <p className="font-mono text-xs text-subtle">created {formatDate(project.createdAt)}</p>
    </header>
  );
}

/** Screen 09 / 15, P1 slice: project header with environments, the apps list and the inline "Connect app" form. */
export function ProjectPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const projects = useProjects();
  const project = projects.data?.find((p) => p.slug === slug);

  if (projects.isLoading && !projects.data) return <ProjectPageSkeleton />;
  if (projects.isError && !projects.data) return <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />;
  if (!project) return <ProjectNotFound slug={slug} />;
  return (
    <div className="flex flex-col gap-6">
      <ProjectHeader project={project} />
      <AppsSection project={project} />
    </div>
  );
}

function HeaderSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-3 w-24" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-20" />
      </div>
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

function AppsCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex items-center justify-between pb-5">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-64" />
        </div>
        <Skeleton className="h-9 w-28" />
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-[148px] rounded-lg" />
        ))}
      </CardContent>
    </Card>
  );
}

function ProjectPageSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true">
      <HeaderSkeleton />
      <AppsCardSkeleton />
    </div>
  );
}
